<template>
  <span hidden aria-hidden="true"></span>
</template>

<script setup>
import { getPersonDisplayName } from '~/composables/useUtils'

const props = defineProps({
  doc: {
    type: Object,
    required: true,
  },
})
const siteConfig = useSiteConfig()

const normalizeText = (value) =>
  value
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;|&#160;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'")
    .replace(/&[a-z]+;|&#\d+;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim()

const firstNonEmptyText = (values) => {
  for (const value of values) {
    if (typeof value !== 'string') {
      continue
    }

    const normalizedValue = normalizeText(value)
    if (normalizedValue) {
      return normalizedValue
    }
  }

  return ''
}

const truncateText = (value, maxLength = 160) => {
  if (value.length <= maxLength) {
    return value
  }

  const shortenedText = value.slice(0, maxLength - 1)
  const lastSpace = shortenedText.lastIndexOf(' ')

  return `${shortenedText.slice(0, lastSpace > 0 ? lastSpace : undefined).trim()}…`
}

const displayName = computed(() => {
  if (!props.doc.person_id) {
    return props.doc.name
  }

  return getPersonDisplayName(props.doc.person_title, props.doc.name)
})

const title = firstNonEmptyText([props.doc.seo_title, displayName.value, props.doc.page_title])

const webContentDescriptions = props.doc.web_sections?.flatMap((section) =>
  section.web_contents?.flatMap((content) => [
    content.summary,
    content.description,
    content.body,
    content.name,
  ])
)

const descriptionSource = firstNonEmptyText([
  props.doc.seo_description,
  props.doc.overview_note,
  props.doc.summary,
  props.doc.description,
  props.doc.body,
  props.doc.content,
  ...(webContentDescriptions || []),
])

const description = descriptionSource
  ? truncateText(descriptionSource)
  : `Explore ${title} at the South African Theological Seminary (SATS).`

const socialImageURL = props.doc.img
  ? new URL(`/media/${props.doc.img}.webp`, siteConfig.url).href
  : undefined

const socialImageMeta = socialImageURL
  ? {
      ogImage: socialImageURL,
      ogImageAlt: title,
      twitterCard: 'summary_large_image',
      twitterImage: socialImageURL,
      twitterImageAlt: title,
    }
  : {}

useSeoMeta({
  title,
  description,
  ogTitle: title,
  ogDescription: description,
  twitterTitle: title,
  twitterDescription: description,
  ...socialImageMeta,
})
</script>
